import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import LoginForm from '../components/LoginForm';
import AirbnbCalendar from '../components/AirbnbCalendar';
import useIcalUnavailableRanges from '../hooks/useIcalUnavailableRanges';
import i18n from '../i18n';
import { clearAuth, getAuthRole, setAuthRole, subscribeAuthRole } from '../utils/auth';
import './GestionePulizie.css';

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const toYmd = (date) => {
  const d = normalizeDate(date);
  return d.toISOString().slice(0, 10);
};

const reservationKeyForRange = (range) => {
  if (!range?.start || !range?.end) {
    return '';
  }
  return `${toYmd(range.start)}_${toYmd(range.end)}`;
};

const diffDaysUtc = (startDate, endDate) => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((utcEnd - utcStart) / (1000 * 60 * 60 * 24));
};

const PASSWORD_ROLES = {
  enzo10: 'admin',
  Gianni58: 'admin',
  cleaningRita: 'cleaning'
};

const GestionePulizie = () => {
  const [authRole, setAuthRoleState] = useState(() => getAuthRole());

  const isAllowed = authRole === 'admin' || authRole === 'cleaning';

  useEffect(() => {
    i18n.changeLanguage('it');
  }, []);

  useEffect(() => {
    return subscribeAuthRole(setAuthRoleState);
  }, []);

  const handleLogin = (role) => {
    if (role !== 'admin' && role !== 'cleaning') {
      return;
    }

    setAuthRole(role);
  };

  const handleLogout = () => {
    clearAuth();
  };

  const { ranges, isLoading, error } = useIcalUnavailableRanges({
    withSources: false,
    enabled: isAllowed,
  });

  const [overlays, setOverlays] = useState({});
  const [isLoadingOverlays, setIsLoadingOverlays] = useState(false);
  const [overlaysError, setOverlaysError] = useState('');
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentStatus, setCommentStatus] = useState({});

  useEffect(() => {
    if (!isAllowed) {
      return;
    }

    let isCancelled = false;

    const load = async () => {
      try {
        setIsLoadingOverlays(true);
        setOverlaysError('');

        const response = await fetch('/api/bookings/overlays.php', { cache: 'no-store' });
        const payload = await response.json();
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error || 'Impossibile caricare i dati.');
        }

        const loaded = payload?.data?.overlays || {};
        if (!isCancelled) {
          setOverlays(loaded);
        }
      } catch (err) {
        if (!isCancelled) {
          setOverlaysError(err?.message || 'Errore di caricamento.');
          setOverlays({});
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingOverlays(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [isAllowed]);

  const visibleRanges = useMemo(() => {
    return (ranges || []).filter((range) => {
      const key = reservationKeyForRange(range);
      const overlay = overlays[key];
      return !(overlay && overlay.bookingMode === 'errore');
    });
  }, [overlays, ranges]);

  const upcomingReservations = useMemo(() => {
    const today = normalizeDate(new Date());

    return (visibleRanges || [])
      .filter((range) => {
        if (!range?.end || normalizeDate(range.end) <= today) {
          return false;
        }
        const key = reservationKeyForRange(range);
        const overlay = overlays[key] || null;
        const name = overlay && typeof overlay.name === 'string' ? overlay.name.trim() : '';
        if (!name && range.start && normalizeDate(range.start).getTime() === today.getTime()) {
          return false;
        }
        return true;
      })
      .sort((a, b) => normalizeDate(a.start) - normalizeDate(b.start));
  }, [visibleRanges, overlays]);

  const formatBeds = (beds) => {
    if (!beds || typeof beds !== 'object') {
      return '';
    }

    const labels = [];
    if (beds.matrimoniale) labels.push('matrimoniale');
    if (beds.lettino1) labels.push('lettino 1');
    if (beds.lettino2) labels.push('lettino 2');
    if (beds.divanoLetto) labels.push('divano letto');
    if (beds.lettiniAssemblati) labels.push('lettini assemblati');

    return labels.length > 0 ? labels.join(', ') : '';
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const setDraft = (key, value) => {
    setCommentDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddComment = async (key, range) => {
    const text = (commentDrafts[key] || '').trim();
    if (!text || !key) {
      return;
    }

    const by = authRole === 'cleaning' ? 'Pulizie' : 'Proprietario';
    setCommentStatus((prev) => ({ ...prev, [key]: 'saving' }));

    try {
      const response = await fetch('/api/bookings/overlays.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add_comment', reservationKey: key, text, by }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Errore durante il salvataggio.');
      }

      setOverlays((prev) => ({ ...prev, [key]: payload.data }));
      setDraft(key, '');
      setCommentStatus((prev) => ({ ...prev, [key]: 'saved' }));
      window.setTimeout(() => {
        setCommentStatus((prev) => ({ ...prev, [key]: '' }));
      }, 1500);

      const startLabel = range?.start ? formatDate(range.start) : '';
      const endLabel = range?.end ? formatDate(range.end) : '';
      // Même type que les commentaires admin (`reservation_comment`) pour réutiliser
      // le routage n8n → Telegram déjà en place ; le 🧹 et « 👤 Pulizie » identifient
      // qu'il vient de la femme de ménage. Envoyé APRÈS la sauvegarde réussie.
      fetch('https://n8n.atoneo.com/webhook/commentaire-site', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({
          type: 'reservation_comment',
          message: `🧹 *Nuovo commento pulizie*\n\n💬 ${text}\n\n📅 ${startLabel} → ${endLabel}\n👤 ${by}`,
        }),
      }).catch(() => {
        // Ne bloque pas l'UI si le webhook est indisponible.
      });
    } catch (err) {
      setCommentStatus((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  if (!isAllowed) {
    return (
      <LoginForm
        onLogin={handleLogin}
        passwordRoles={PASSWORD_ROLES}
        labels={{
          title: 'Gestione Pulizie',
          placeholder: 'Inserisci la password',
          submit: 'Accedi',
          errorInvalid: 'Password non corretta'
        }}
      />
    );
  }

  const nextKey = upcomingReservations.length > 0 ? reservationKeyForRange(upcomingReservations[0]) : null;

  return (
    <div className="site-shell admin-shell">
      <AdminSidebar
        onLogout={handleLogout}
        sitePath="/it/affitto-villa-appartamento-sardegna-san-teodoro"
        role={authRole}
      />
      <main className="site-shell-content admin-content">
        <div className="admin-panel pulizie-panel">

          <h2>Gestione Pulizie</h2>

          {(isLoading || isLoadingOverlays) && <p className="pulizie-loading">Caricamento…</p>}
          {!isLoading && error && <p className="admin-error">{error}</p>}
          {!isLoadingOverlays && overlaysError && <p className="admin-error">{overlaysError}</p>}

          {!isLoading && !error && upcomingReservations.length === 0 && (
            <p className="pulizie-empty">Nessuna prenotazione trovata.</p>
          )}

          {!isLoading && !error && upcomingReservations.length > 0 && (
            <ul className="pulizie-list">
              {upcomingReservations.map((range, index) => {
                const nights = diffDaysUtc(range.start, range.end);
                const key = reservationKeyForRange(range);
                const overlay = overlays[key] || null;
                const adults = overlay && Number.isFinite(overlay.adults) ? overlay.adults : 0;
                const children = overlay && Number.isFinite(overlay.children) ? overlay.children : 0;
                const peopleTotal = adults + children;
                const bedsText = formatBeds(overlay?.beds);
                const notes = overlay && typeof overlay.notes === 'string' ? overlay.notes.trim() : '';
                const comments = Array.isArray(overlay?.comments) ? overlay.comments : [];
                const isNext = key === nextKey;

                const nextRange = upcomingReservations[index + 1];
                const hasConflict =
                  nextRange &&
                  normalizeDate(range.end).getTime() === normalizeDate(nextRange.start).getTime();

                return (
                  <React.Fragment
                    key={`${range.start?.toISOString?.() || index}-${range.end?.toISOString?.() || index}`}
                  >
                    <li
                      className={`pulizie-card${isNext ? ' pulizie-card--next' : ''}`}
                    >
                    {isNext && <span className="pulizie-card-badge">Prossimo</span>}

                    <div className="pulizie-card-dates">
                      <div className="pulizie-card-date-block">
                        <span className="pulizie-card-date-label">Arrivo</span>
                        <span className="pulizie-card-date-value">{formatDate(range.start)}</span>
                      </div>
                      <span className="pulizie-card-sep">→</span>
                      <div className="pulizie-card-date-block">
                        <span className="pulizie-card-date-label">Partenza</span>
                        <span className="pulizie-card-date-value">{formatDate(range.end)}</span>
                      </div>
                      <div className="pulizie-card-meta">
                        <span className="pulizie-chip">{nights} notti</span>
                        {peopleTotal > 0 && <span className="pulizie-chip">{peopleTotal} pers.</span>}
                        {notes && (
                          <span className="pulizie-note-dot" title={notes} aria-label="Note présentes">
                            📋
                          </span>
                        )}
                      </div>
                    </div>

                    {(adults > 0 || children > 0) && (
                      <div className="pulizie-card-people">
                        {adults > 0 && <span className="pulizie-chip pulizie-chip--info">{adults} adulti</span>}
                        {children > 0 && <span className="pulizie-chip pulizie-chip--info">{children} bambini</span>}
                      </div>
                    )}

                    {bedsText ? (
                      <div className="pulizie-card-row">
                        <span className="pulizie-card-row-label">Letti da preparare</span>
                        <span className="pulizie-card-row-value">{bedsText}</span>
                      </div>
                    ) : null}

                    {notes ? (
                      <div className="pulizie-card-row pulizie-card-row--notes">
                        <span className="pulizie-card-row-label">Note</span>
                        <span className="pulizie-card-row-value">{notes}</span>
                      </div>
                    ) : null}

                    <div className="pulizie-card-comments">
                      <span className="pulizie-card-row-label">Commenti</span>
                      {comments.length > 0 ? (
                        <ul className="pulizie-comments-list">
                          {comments.map((c, ci) => (
                            <li key={`${c.at || ci}-${ci}`} className="pulizie-comment">
                              <span className="pulizie-comment-text">{c.text}</span>
                              <span className="pulizie-comment-meta">
                                {c.by ? `${c.by} · ` : ''}
                                {c.at ? new Date(c.at).toLocaleDateString('it-IT') : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="pulizie-comments-empty">Nessun commento</span>
                      )}
                      <div className="pulizie-comment-add">
                        <input
                          type="text"
                          className="pulizie-comment-input"
                          placeholder="Aggiungi un commento…"
                          value={commentDrafts[key] || ''}
                          onChange={(e) => setDraft(key, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddComment(key, range);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="pulizie-comment-btn"
                          onClick={() => handleAddComment(key, range)}
                          disabled={commentStatus[key] === 'saving' || !(commentDrafts[key] || '').trim()}
                        >
                          {commentStatus[key] === 'saving' ? '…' : 'Invia'}
                        </button>
                        {commentStatus[key] === 'saved' && (
                          <span className="pulizie-comment-ok">✓</span>
                        )}
                        {commentStatus[key] === 'error' && (
                          <span className="pulizie-comment-error">Errore</span>
                        )}
                      </div>
                    </div>
                  </li>

                  {hasConflict && (
                    <li className="pulizie-conflict-alert" role="alert">
                      <span className="pulizie-conflict-alert-icon">⚠️</span>
                      <span>
                        Arrivo lo stesso giorno della partenza —{' '}
                        <strong>nessuna notte libera</strong> tra i due soggiorni
                      </span>
                    </li>
                  )}
                </React.Fragment>
                );
              })}
            </ul>
          )}

          <details className="pulizie-calendar-details" open>
            <summary className="pulizie-calendar-summary">Calendario</summary>
            <div className="pulizie-calendar-body">
              <AirbnbCalendar
                lang="it"
                readOnly={true}
                showPrices={false}
                alternateReservedPeriodColors={true}
                withSources={false}
                showSourceBadges={false}
                unavailableRanges={visibleRanges}
                showOwnerStayBadge={false}
              />
            </div>
          </details>

        </div>
      </main>
    </div>
  );
};

export default GestionePulizie;
